import { Text, TextVariant } from '@naf/text';
import { Table, TableBody, TableCell, TableRow } from '@naf/table';
import React, { useEffect, useState } from 'react';
import { useWindowResize } from '../../../../../../hooks/Window/useWindowResize';
import { ArticleBlockContentGridCol } from '../ArticleBlockContentGridCol';
import { BlockContentContext } from '../../../../BlockContent.utils';
import { TableSerializerProps } from './Table.types';
import { CellContent, withWordBreakHints } from './Table.utils';
import * as S from './Table.styles';

const NafCenterTableView = ({ value: { table, shouldCollapse, caption } }: TableSerializerProps) => {
  const [bodyWidth, setBodyWidth] = useState<number>();
  const [shouldRowsCollapse, setShouldRowsCollapse] = useState<boolean>(shouldCollapse);

  const setWidth = () => {
    if (document) {
      const element = document.querySelector('main');
      if (element) {
        setBodyWidth(element.offsetWidth);
      }
    }
  };

  useEffect(() => {
    setWidth();
  }, []);

  useWindowResize(setWidth);

  return (
    <S.TableWrapper width={bodyWidth}>
      <h2>{caption}</h2>
      <Table>
        {table.rows.map((row: any, index: number) => (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <>
            {index <= 10 && (
              <S.Tr index={index}>
                {row.cells.map((cell: string) =>
                  index === 0 ? <S.Th>{cell}</S.Th> : <S.Td>{CellContent(cell) || ''}</S.Td>,
                )}
              </S.Tr>
            )}
          </>
        ))}
      </Table>
      <S.ShowMoreWrapper $isHidden={shouldRowsCollapse}>
        <S.StyledTable>
          {table.rows.map((row: any, index: number) => (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {index > 10 && (
                <S.Tr>
                  {row.cells.map((cell: string) => (
                    <S.Td>{CellContent(cell) || ''}</S.Td>
                  ))}
                </S.Tr>
              )}
            </>
          ))}
        </S.StyledTable>
      </S.ShowMoreWrapper>
      {shouldRowsCollapse && table.rows.length > 10 && (
        <S.StyledButtonLink text="Se alle testpunker +" onClick={() => setShouldRowsCollapse(false)} />
      )}
    </S.TableWrapper>
  );
};

const TableBlock = ({
  value,
  value: { title, table, orientation, shouldCollapse, caption, displayStyle },
}: TableSerializerProps) => {
  if (displayStyle === 'nafCenter') {
    return <NafCenterTableView value={value} />;
  }

  // Force a minimum column width so columns get room to breathe instead of squeezing text; the
  // table's Outer wrapper (overflow-x: auto) then kicks in to make the rest scrollable on mobile.
  const columnCount = table?.rows?.length ? Math.max(...table.rows.map((row) => row.cells.length)) : 0;
  const tableMinWidth = columnCount > 0 ? `${Math.max(340, columnCount * 140)}px` : undefined;

  return (
    <>
      <Text variant={TextVariant.Header2}>{title}</Text>
      <S.StyledFigure>
        <S.ExpandedTable shouldCollapse={shouldCollapse} variant="vertical" minWidth={tableMinWidth}>
          <TableBody>
            {table &&
              table.rows &&
              table.rows.map((row, rowIndex) => (
                <TableRow key={row._key}>
                  {row.cells.map((cell, index) => {
                    let role: 'td' | 'th' = 'td';
                    let align: 'left' | 'right' | 'center' = 'left';
                    if (orientation === 'top') {
                      if (rowIndex === 0) {
                        role = 'th';
                      }
                      if (index > 0) {
                        align = 'right';
                      }
                    }

                    if (orientation === 'left') {
                      if (index === 0) {
                        role = 'th';
                      }
                      if (index > 0) {
                        align = 'right';
                      }
                    }
                    if (orientation === 'crossTable') {
                      if (index === 0 || rowIndex === 0) {
                        role = 'th';
                      }
                      if (index > 0) {
                        align = 'right';
                      }
                    }
                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <TableCell key={`${row._key}-${index}`} align={align} as={role}>
                        <S.CellText>{withWordBreakHints(cell)}</S.CellText>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
          </TableBody>
        </S.ExpandedTable>
        {orientation === 'left' &&
          table &&
          table.rows &&
          table.rows.map((row) => (
            <S.CollapsedTable
              headingLevel={3}
              key={`left-table-collapsed-key-${row._key}`}
              $shouldCollapse={shouldCollapse}
              title={row.cells[0]}
            >
              {row.cells.slice(1).map((cell, cellIndex) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`left-table-collapsed-key-${row._key}-${cellIndex}`}>{cell}</div>
              ))}
            </S.CollapsedTable>
          ))}
        {orientation === 'crossTable' &&
          table &&
          table.rows &&
          table.rows.slice(1).map((row) => (
            <S.CollapsedTable
              headingLevel={3}
              key={`cross-table-collapsed-${row._key}`}
              $shouldCollapse={shouldCollapse}
              title={row.cells[0]}
            >
              {row.cells.slice(1).map((cell, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Table key={`cross-table-collapsed-${row._key}-${index}`}>
                  <TableRow>
                    <TableCell as="th" align="left">
                      <S.CellText>{withWordBreakHints(table.rows[0].cells.slice(1)[index])}</S.CellText>
                    </TableCell>
                    <TableCell align="right">
                      <S.CellText>{withWordBreakHints(cell)}</S.CellText>
                    </TableCell>
                  </TableRow>
                </Table>
              ))}
            </S.CollapsedTable>
          ))}
        {orientation === 'top' &&
          table &&
          table.rows &&
          table.rows[0].cells.map((columnHeader, columnIndex) => (
            <S.CollapsedTable
              headingLevel={3}
              key={`top-table-collapsed-${columnHeader}`}
              $shouldCollapse={shouldCollapse}
              title={columnHeader}
            >
              {table.rows.slice(1).map((row) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`top-table-collapsed-${columnIndex}-${row._key}`}>{row.cells[columnIndex]}</div>
              ))}
            </S.CollapsedTable>
          ))}
        {caption && <S.Caption>{caption}</S.Caption>}
      </S.StyledFigure>
    </>
  );
};

export const TableSerializer = (context: BlockContentContext = BlockContentContext.Stripped) => {
  if (context === BlockContentContext.Article) {
    return ({ value }: TableSerializerProps) => (
      <ArticleBlockContentGridCol>
        <TableBlock value={value} />
      </ArticleBlockContentGridCol>
    );
  }
  return ({ value }: TableSerializerProps) => <TableBlock value={value} />;
};
